import type React from 'react';
import type { JSX } from 'react';
import dynamic from 'next/dynamic';
import useBreakpoint from 'hooks/useBreakpoint';

// Interfaces
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import type { MediaItem, Plea as PleaProps } from 'interfaces/content/Media';
import type { ListTout } from 'interfaces/content/Ranking';
import { SectionVariant } from 'interfaces/Section';
import type { FeaturedSecondaryProps } from 'components/Home/FeaturedSecondary';
import type { PresentedByProps } from 'components/PresentedBy';

// Constants
import { Microsites } from 'constants/constants';
import getCommonTestId from 'constants/testsIds/common';
import { UIFragments } from 'utils/log/constants/uiFragments';

// Utils
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';

// Components
import ProductHomePageInStream from 'components/Ad/AdSlot/ProductHomePageInStream';
import ErrorBoundary from 'components/ErrorBoundary';

// Styles
import { DesktopCryptoCrashCourseTout } from 'icons/DesktopCryptoCrashCourseTout';
import { MobileCryptoCrashCourseTout } from 'icons/MobileCryptoCrashCourseTout';
import * as RS from 'styles/rightRail';
import HubHeader from 'components/HubHeader';
import type { MenuItemProps } from 'interfaces/navigation/Header';
import type { BreadcrumbsProps } from 'interfaces/navigation/Breadcrumbs';
import * as S from './ProductHomePageStyles';

const FeaturedSecondary = dynamic(() => import('components/Home/FeaturedSecondary'));
const Carousel = dynamic(() => import('components/Carousel'));
const PresentedBy = dynamic(() => import('components/PresentedBy'));
const Link = dynamic(() => import('components/Globals/Base/Link'));
const Plea = dynamic(() => import('components/Home/Plea'));
const Breadcrumbs = dynamic(() => import('components/Breadcrumbs'));

const ExploreModule = dynamic(() => import('components/ExploreModule'), {
  ssr: false,
});
const LatestArticles = dynamic(() => import('components/recommends/ArticleSection/LatestArticles'), {
  ssr: false,
});

export interface ProductHomePageProps {
  carousel: ArticlePreview[];
  featuredArticles?: FeaturedSecondaryProps;
  features: FeaturedSecondaryProps[];
  tags?: FeaturedSecondaryProps[];
  sections: {
    name: string;
    image: MediaItem;
    url: string;
  }[];
  presentedBy?: PresentedByProps;
  plea?: PleaProps;
  sidebar: JSX.Element[];
  disclosure?: JSX.Element;
  hideAds?: boolean;
  platform: string;
  tout?: ListTout;
  pagination?: PagePagination;
  latestArticlesSection?: ArticlePreview[];
  subMenu?: MenuItemProps[];
  title: string;
  breadcrumbs: BreadcrumbsProps;
}

const ProductHomePage: React.FC<ProductHomePageProps> = ({
  carousel,
  featuredArticles,
  features,
  sections,
  presentedBy,
  sidebar,
  hideAds,
  plea,
  platform,
  tags,
  tout,
  title,
  pagination,
  latestArticlesSection,
  subMenu,
  breadcrumbs,
}) => {
  const isDesktop = useBreakpoint('hybridMin');
  const isMobile = useBreakpoint('mdMax');
  const showMobileTout = useBreakpoint('mmMax');
  const isTablet = isMobile === false && isDesktop === false;
  const renderTout = tout && tout.showTout === 1;
  const renderMiddleAd = !isMobile && !hideAds && ![Microsites.Europe, Microsites.Asia].includes(platform);
  const toutHref = renderTout && tout ? tout.toutUrl : '';
  const toutDesktopUrl = renderTout && tout ? tout.desktopImage : '';
  const toutMobileUrl = renderTout && tout ? tout.mobileImage : '';
  const toutOpenInNewTab = renderTout && tout ? tout.openInNewTab : 0;
  let excludedArticles: number[] = [];
  const hasSubMenu = subMenu && subMenu.length > 0;

  const breadcrumbsData = breadcrumbs?.items.length
    ? [{ label: 'home', link: '/' }, ...breadcrumbs.items]
    : [{ label: 'home', link: '/' }];

  features.forEach((feat) => {
    const articlesIdsList = feat.articles.map((ar) => ar.databaseId);
    excludedArticles = [...excludedArticles, ...articlesIdsList] as number[];
  });

  featuredArticles?.articles.forEach((article) => {
    excludedArticles = [...excludedArticles, article.databaseId];
  });

  return (
    <S.Wrapper>
      {presentedBy && (
        <PresentedBy
          {...presentedBy}
          className='presented-by-banner'
        />
      )}
      {hasSubMenu && (
        <S.Header>
          <Breadcrumbs
            items={breadcrumbsData}
            isHubPage
          />
          <HubHeader
            title={title}
            subMenu={subMenu}
          />
        </S.Header>
      )}
      <S.ContentWrapper
        id='content'
        className={`${platform}`}
      >
        <S.Content>
          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            <Carousel
              articles={carousel}
              dataVariant='product-home-carousel'
            />
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {renderTout && (
              <S.ToutContainer>
                <Link
                  href={toutHref}
                  type='image'
                  aria-label={showMobileTout ? tout?.mobileImageAlt : tout?.desktopImageAlt}
                  target={toutOpenInNewTab ? '_blank' : ''}
                >
                  <S.Tout
                    $url={showMobileTout ? toutMobileUrl : toutDesktopUrl}
                    className={`img ${platform}`}
                  />
                </Link>
              </S.ToutContainer>
            )}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {isMobile && <S.SidebarMobile>{sidebar.slice(0, 2)}</S.SidebarMobile>}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {featuredArticles && (
              <FeaturedSecondary
                className='features'
                dataCy={getCommonTestId('THE_LATEST_MODULE')}
                type='product-home'
                {...featuredArticles}
              />
            )}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {plea && (
              <Plea
                {...plea}
                className={`${platform}`}
              />
            )}
          </ErrorBoundary>

          {Microsites.Crypto === platform ? (
            <S.Tout
              data-cy={getCommonTestId('CRASH_COURSE_TOUT')}
              className='crypto'
            >
              <Link
                href='/section/crash-course/'
                type='image'
              >
                {isMobile ? <MobileCryptoCrashCourseTout /> : <DesktopCryptoCrashCourseTout />}
              </Link>
            </S.Tout>
          ) : null}
          {isMobile && Microsites.Crypto === platform && (
            <S.ToutButton>
              <Link
                className='black-button'
                href='/section/crash-course/'
                type='primaryButton'
                size={{ default: 'large' }}
              >
                Discover
              </Link>
            </S.ToutButton>
          )}

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {sections.length > 0 && (
              <ExploreModule
                heading='Explore'
                sections={sections}
              />
            )}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {renderMiddleAd && <ProductHomePageInStream id={0} />}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {isMobile && sidebar.length > 2 && <S.SidebarMobile>{sidebar.slice(2)}</S.SidebarMobile>}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {features.map((feature) => (
              <FeaturedSecondary
                dataCy={getCommonTestId('HOMEPAGE_FEATURED_SECTIONS')}
                className='features'
                hideAds={hideAds}
                key={feature.heading}
                {...feature}
              />
            ))}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {tags &&
              tags.map((tag) => (
                <FeaturedSecondary
                  dataCy={getCommonTestId('HOMEPAGE_FEATURED_SECTIONS')}
                  className='features'
                  key={tag.heading}
                  {...tag}
                />
              ))}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {isTablet && (
              <>
                {!hideAds && <ProductHomePageInStream id={1} />}
                <S.SidebarTablet>{sidebar}</S.SidebarTablet>
              </>
            )}
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.WELL_REGION}>
            {(platform === Microsites.Well || platform === Microsites.Crypto) && (
              <LatestArticles
                title='More Articles'
                variant={SectionVariant.MoreArticles}
                articles={latestArticlesSection}
                platform={`${platform}`}
                // The bang! is just to skip pagination being undefined, safe to do this because we only used in well and crypto and it will always be there
                pagination={pagination}
                pinnedLinks={null}
                trackingData={{
                  articleNameCD: `${platform} Homepage `,
                  contentPlacementCD: `${platform} - Homepage - grid - row`,
                }}
              />
            )}
          </ErrorBoundary>
        </S.Content>

        <ErrorBoundary fragment={UIFragments.WELL_REGION}>
          {isDesktop && (
            <RS.RightRail
              data-cy={getCommonTestId('HOMEPAGE_RIGHT_RAIL')}
              className={`${platform}`}
            >
              {sidebar}
            </RS.RightRail>
          )}
        </ErrorBoundary>
      </S.ContentWrapper>
    </S.Wrapper>
  );
};

export default ProductHomePage;
